import { useEffect, useState } from 'react';
import { Input, ModalForm } from '@/contasis';
import type { AccountingAccount, AccountingAccountInput } from '../../../modelos/CuentaContable';
import { formValidation, commonRules, type Validators } from '@/utils';

interface AccountingAccountModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: AccountingAccount;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (input: AccountingAccountInput) => Promise<void>;
}

const crearValidadoresIniciales = (): Validators => ({
  code: formValidation.createFieldValidator([
    commonRules.required('Ingresa el código contable'),
    commonRules.maxLength(12, 'Máximo 12 dígitos'),
    commonRules.custom(
      (value: string) => /^\d+$/.test(value),
      'El código solo debe contener dígitos'
    ),
  ]),
});

export function AccountingAccountModal({
  isOpen,
  mode,
  initialData,
  errorMessage,
  onClose,
  onSubmit
}: AccountingAccountModalProps) {
  const [code, setCode] = useState('');
  const [validators, setValidators] = useState<Validators>(crearValidadoresIniciales);
  const [serverError, setServerError] = useState<string | null>(null);
  const [esGuardando, setEnviando] = useState(false);

  useEffect(() => {
    if (isOpen && initialData) {
      setCode(initialData.code ?? '');
      const validadoresIniciales = crearValidadoresIniciales();
      if (initialData.code) {
        validadoresIniciales.code.state = initialData.code;
        validadoresIniciales.code.valid = true;
      }
      setValidators(validadoresIniciales);
      setServerError(null);
    } else if (isOpen) {
      setCode('');
      setValidators(crearValidadoresIniciales());
      setServerError(null);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const sanitizeDigits = (value: string) => value.replace(/\D+/g, '').slice(0, 12);

  const actualizarCampo = (value: string) => {
    setCode(value);
    setServerError(null);
    const updatedValidators = formValidation.updateValidators('code', value, validators);
    setValidators(updatedValidators);
  };

  const validarFormulario = (): boolean => {
    const result = formValidation.isFormValid(validators, { code } as Record<string, string>);
    if (result.validators) {
      setValidators({ ...result.validators });
    }
    return result.status;
  };

  const onSubmitGuardar = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setEnviando(true);
    setServerError(null);
    try {
      await onSubmit({ code: code.trim() });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar la cuenta contable';
      setServerError(message);
    } finally {
      setEnviando(false);
    }
  };

  const fieldError = formValidation.getFieldErrors('code', validators)[0] || serverError || errorMessage;
  const title = mode === 'create' ? 'Agregar cuenta contable' : 'Editar cuenta contable';

  return (
    <ModalForm
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      onSubmit={onSubmitGuardar}
      submitLabel="Guardar"
      isSubmitting={esGuardando}
      size="small"
    >
        <Input
          id="accounting-code"
          label="Código de cuenta contable"
          required
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={code}
          onChange={(event) => actualizarCampo(sanitizeDigits(event.target.value))}
          placeholder="Ej. 1041"
          maxLength={12}
          helperText={!fieldError ? 'Solo dígitos, máximo 12. Se conserva el formato exacto (sin perder ceros).' : undefined}
          error={fieldError || undefined}
        />
    </ModalForm>
  );
}
